import Card from '../Card'
import { createUseStyles } from 'react-jss'
import { Colors } from '../../constants/colors'
import { TransactionListItemModel } from '../../redux/types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircle } from '@fortawesome/free-solid-svg-icons'
import { formatDateString, formatDateTimeString } from '../../utils/dateHelper'
import { formatMoney } from '../../utils/moneyHelper'
import { truncate } from '../../utils/stringHelper'
import Pagination from '../Pagination'
import { useNavigate } from 'react-router-dom'

interface Props {
  transactionsOnPage: TransactionListItemModel[]
  pageIndex: number
  pageSize: number
  totalCount: number
  onPageChange: (pageIndex: number) => void
}

const TransactionList = ({ transactionsOnPage, pageIndex, pageSize, totalCount, onPageChange }: Props) => {
  const styles = useStyles()
  const navigate = useNavigate()

  const renderHeader = () => {
    return (
      <Card className={`${styles.container} ${styles.header} card`}>
        <div className={styles.cell}>
          <h6>Date</h6>
        </div>
        <div className={`${styles.cell} ${styles.amount}`}>
          <h6>Amount</h6>
        </div>
        <div className={styles.cell}>
          <h6>Type</h6>
        </div>
        <div className={styles.cell}>
          <h6>User</h6>
        </div>
        <div className={styles.cell}>
          <h6>Payday</h6>
        </div>
        <div className={styles.cell}>
          <h6>Customer</h6>
        </div>
        <div className={`${styles.cell} ${styles.stateIndicator}`}>
          <h6>State</h6>
        </div>
      </Card>
    )
  }

  const renderListItems = () => {
    return transactionsOnPage?.map((item, index) => {
        return (
          <Card
            key={`transactionListItem${index}`}
            className={`${styles.container} ${styles.row} card`}
            onClick={() => navigate(`/transactions/${item.id}`)}
          >
              <div className={styles.cell}>
                {formatDateTimeString(item.date)}
              </div>
              <div className={`${styles.cell} ${styles.amount}`}>
                {formatMoney(item.amount)}
              </div>
              <div className={styles.cell} title={item.type.toString()}>
                {truncate(item.type.toString(), 16)}
              </div>
              <div className={styles.cell}>
                {item.userId &&
                  <button
                    className={styles.linkButton}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/users/${item.userId}`)
                    }}
                    title={'View user'}
                  >
                    {item.userGivenName ?? ''} {item.userFamilyName ?? ''}
                  </button>
                }
              </div>
              <div className={styles.cell}>
                {item.paydayDate != null ? formatDateString(item.paydayDate) : ''}
              </div>
              <div className={styles.cell}>
                {item.employerId &&
                  <button
                    className={styles.linkButton}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/employers/${item.employerId}`)
                    }}
                    title={'View employer'}
                  >
                    {item.customerName ?? ''}
                  </button>
                }
                {item.payoutCreditFacilityId &&
                  <button
                    className={styles.linkButton}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/payoutcreditfacilities/${item.payoutCreditFacilityId}`)
                    }}
                    title={'View payout credit facility'}
                  >
                    {item.customerName ?? ''}
                  </button>
                }
              </div>
              <div className={`${styles.cell} ${styles.stateIndicator}`}>
                {renderStateIndicator(item)}
              </div>
            </Card>
        )
      })
    }

    const renderStateIndicator = (transactionListItem: TransactionListItemModel) => {
      let color = Colors.white

      switch(transactionListItem.state) {
        case 'pending':
          color = Colors.lightDark
          break
        case 'cancelled':
          color = Colors.red
          break
        case 'completed':
          color = Colors.euro_600
          break
      }

      return (
        <FontAwesomeIcon
          icon={faCircle}
          color={color}
          title={transactionListItem.state}
        />
      )
    }

  return (
    <div>
      {renderHeader()}
      {renderListItems()}
      <div className={styles.pagination}>
        <Pagination
            pageIndex={pageIndex}
            itemCount={totalCount}
            pageSize={pageSize}
            siblingCount={1}
            onPageChange={onPageChange}
        />
      </div>
  </div>
  )
}

export default TransactionList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    padding: '3px 20px',
  },
  row: {
    margin: '10px 0',
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    }
  },
  linkButton: {
    textAlign: 'left',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '1px',
    borderRadius: '4px',
    display: 'inline-block',
    color: Colors.euro_600,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)'
    },
    '&:focus': {
      outline: `2px solid ${Colors.euro_600}`,
      outlineOffset: '1px'
    }
  },
  header: {
    margin: '30px 0 20px 0',
  },
  cell: {
    flex: 1,
    padding: '0px 10px'
  },
  stateIndicator: {
    textAlign: 'center',
    flex: '0 0 60px'
  },
  amount: {
    flex: '0 0 130px'
  },
  pagination: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center'
  }
})
